import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { UsersTable } from './-components/UsersTable'
import { createColumns } from './-components/columns'
import type {
  AdminUser,
  Department,
  UpdateUserPayload,
} from '@/types/user.types'
import { PageTitle } from '@/components/PageTitle'
import { filterUsersBySearch } from '@/lib/user-search'
import {
  useActivateUser,
  useAllUsers,
  useDeactivateUser,
  useSyncUserHubSpot,
  useUpdateUser,
  useUpdateUserDepartment,
} from '@/hooks/repositories/useUsersRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/users/')({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.users),
})

type UserStatusFilter = 'all' | 'active' | 'inactive' | 'deleted'

function RouteComponent() {
  const [departmentFilter, setDepartmentFilter] = React.useState<
    Department | 'all'
  >('all')
  const [statusFilter, setStatusFilter] =
    React.useState<UserStatusFilter>('all')
  const [searchValue, setSearchValue] = React.useState('')
  const [syncingEmail, setSyncingEmail] = React.useState<string | null>(null)

  // Fetch all users
  const { data: allUsers = [], isLoading } = useAllUsers()

  // Mutations
  const updateUser = useUpdateUser()
  const updateUserDepartment = useUpdateUserDepartment()
  const activateUser = useActivateUser()
  const deactivateUser = useDeactivateUser()
  const syncHubSpot = useSyncUserHubSpot()

  // Client-side filtering
  const filteredUsers = React.useMemo(() => {
    let users: Array<AdminUser> = [...allUsers]

    // Department filter
    if (departmentFilter !== 'all') {
      users = users.filter((u) => u.department === departmentFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          users = users.filter((u) => u.isActive && !u.deletedAt)
          break
        case 'inactive':
          users = users.filter((u) => !u.isActive && !u.deletedAt)
          break
        case 'deleted':
          users = users.filter((u) => !!u.deletedAt)
          break
      }
    }

    // Search filter — tokenized + accent/punctuation insensitive across name,
    // email, title, phone and RightCapital ids (see lib/user-search).
    users = filterUsersBySearch(users, searchValue)

    return users
  }, [allUsers, departmentFilter, statusFilter, searchValue])

  // Column actions
  const columnActions = React.useMemo(
    () => ({
      onUpdateField: (userId: string, field: string, value: string | null) => {
        updateUser.mutate({
          userId,
          payload: { [field]: value } as UpdateUserPayload,
        })
      },
      onUpdateDepartment: (userId: string, department: Department) => {
        updateUserDepartment.mutate({ userId, department })
      },
      onActivate: (userId: string) => {
        activateUser.mutate(userId)
      },
      onDeactivate: (userId: string, deleted?: boolean) => {
        deactivateUser.mutate({ userId, deleted })
      },
      onSyncHubSpot: (email: string) => {
        setSyncingEmail(email)
        syncHubSpot.mutate(email, {
          onSettled: () => setSyncingEmail(null),
        })
      },
      syncingEmail,
    }),
    [
      updateUser,
      updateUserDepartment,
      activateUser,
      deactivateUser,
      syncHubSpot,
      syncingEmail,
    ],
  )

  const columns = React.useMemo(
    () => createColumns(columnActions),
    [columnActions],
  )

  return (
    <div className="py-4">
      <PageTitle title="Users" />
      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground mt-2">
          Manage all platform users. Click on any field to edit.
        </p>
      </div>

      <UsersTable
        data={filteredUsers}
        columns={columns}
        isLoading={isLoading}
        totalCount={allUsers.length}
        departmentFilter={departmentFilter}
        statusFilter={statusFilter}
        searchValue={searchValue}
        onDepartmentFilterChange={setDepartmentFilter}
        onStatusFilterChange={setStatusFilter}
        onSearchChange={setSearchValue}
      />
    </div>
  )
}
